import { useEffect, useState, useCallback } from 'react'
import { api } from '../services'
import { Domain, DNSRecord, DNSProvider, DNSRecordInput } from '@tools-set/shared-types'
import DNSRecordDialog from '../components/DNSRecordDialog'
import Card from '../components/Card'
import Select from '../components/Select'
import type { SelectOption } from '../components/Select'

// 合并后的域名类型
interface MergedDomain {
  name: string
  domains: Domain[]
  status: 'active' | 'paused' | 'pending'
}

// DNS 记录缓存类型
interface DNSRecordCache {
  [domainName: string]: {
    records: DNSRecord[]
    timestamp: number
    loading?: boolean
  }
}

export default function DNSManagement() {
  const [mergedDomains, setMergedDomains] = useState<MergedDomain[]>([])
  const [selectedDomains, setSelectedDomains] = useState<Domain[]>([])
  const [recordsCache, setRecordsCache] = useState<DNSRecordCache>({})
  const [records, setRecords] = useState<DNSRecord[]>([])
  const [filteredRecords, setFilteredRecords] = useState<DNSRecord[]>([])
  const [initialLoading, setInitialLoading] = useState(false)
  const [recordsLoading, setRecordsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedProvider, setSelectedProvider] = useState<DNSProvider | 'all'>('all')

  // 对话框状态
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [editingRecord, setEditingRecord] = useState<DNSRecord | null>(null)

  // 加载域名列表并预加载所有 DNS 记录
  useEffect(() => {
    loadDomainsAndRecords()
  }, [])

  // 筛选记录
  useEffect(() => {
    let filtered = records

    // 按提供商筛选
    if (selectedProvider !== 'all') {
      filtered = filtered.filter((r) => r.provider === selectedProvider)
    }

    // 按搜索词筛选
    if (searchTerm) {
      const term = searchTerm.toLowerCase()
      filtered = filtered.filter(
        (r) =>
          r.name.toLowerCase().includes(term) ||
          r.content.toLowerCase().includes(term) ||
          r.type.toLowerCase().includes(term)
      )
    }

    setFilteredRecords(filtered)
  }, [records, searchTerm, selectedProvider])

  // 加载域名列表并预加载所有 DNS 记录
  const loadDomainsAndRecords = async () => {
    setInitialLoading(true)
    setError(null)
    try {
      const response = await api.getDomains()
      if (response.success && response.data) {
        // 合并相同域名的记录
        const domainMap = new Map<string, Domain[]>()
        response.data.domains.forEach(domain => {
          if (!domainMap.has(domain.name)) {
            domainMap.set(domain.name, [])
          }
          domainMap.get(domain.name)!.push(domain)
        })
        
        const merged: MergedDomain[] = Array.from(domainMap.entries()).map(([name, domains]) => ({
          name,
          domains,
          status: domains.some(d => d.status === 'active') ? 'active' : domains[0].status
        }))
        
        // 先设置域名列表，让用户能看到
        setMergedDomains(merged)
        setInitialLoading(false)

        // 在后台异步加载所有域名的 DNS 记录
        preloadAllRecords(merged)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '加载域名失败')
      setInitialLoading(false)
    }
  }

  // 预加载所有域名的 DNS 记录
  const preloadAllRecords = async (domains: MergedDomain[]) => {
    // 设置所有域名为加载中状态
    const initialCache: DNSRecordCache = {}
    domains.forEach(domain => {
      initialCache[domain.name] = {
        records: [],
        timestamp: Date.now(),
        loading: true
      }
    })
    setRecordsCache(initialCache)
    
    // 并行加载所有域名的记录
    const promises = domains.map(async (mergedDomain) => {
      try {
        const recordsPromises = mergedDomain.domains.map(domain => 
          api.getDNSRecords(domain.provider, domain.id)
        )
        const responses = await Promise.all(recordsPromises)
        
        // 合并所有记录
        const allRecords: DNSRecord[] = []
        responses.forEach(response => {
          if (response.success && response.data) {
            allRecords.push(...response.data.records)
          }
        })
        
        // 更新单个域名的缓存
        setRecordsCache(prev => ({
          ...prev,
          [mergedDomain.name]: {
            records: allRecords,
            timestamp: Date.now(),
            loading: false
          }
        }))
        
        // 如果当前选中的域名正好是这个，则同步更新显示的记录
        setSelectedDomains(currentDomains => {
          if (currentDomains.length > 0 && currentDomains[0].name === mergedDomain.name) {
            setRecords(allRecords)
          }
          return currentDomains
        })
      } catch (err) {
        console.error(`Failed to load records for ${mergedDomain.name}:`, err)
        // 标记加载失败
        setRecordsCache(prev => ({
          ...prev,
          [mergedDomain.name]: {
            records: [],
            timestamp: Date.now(),
            loading: false
          }
        }))
        
        // 如果当前选中的域名正好是这个，则同步更新
        setSelectedDomains(currentDomains => {
          if (currentDomains.length > 0 && currentDomains[0].name === mergedDomain.name) {
            setRecords([])
          }
          return currentDomains
        })
      }
    })

    await Promise.all(promises)
  }

  // 从缓存中选择域名记录
  const selectDomain = useCallback((domains: Domain[]) => {
    const domainName = domains[0].name
    setSelectedDomains(domains)
    
    // 从缓存中获取记录
    const cached = recordsCache[domainName]
    if (cached) {
      // 即使是 loading 状态，也设置当前的 records（可能是空数组）
      // 加载完成后会自动更新
      setRecords(cached.records)
    } else {
      // 如果缓存中没有，则加载（这种情况理论上不应该发生）
      setRecords([])
      loadRecordsForDomain(domains)
    }
  }, [recordsCache])

  // 加载特定域名的记录（用于刷新）
  const loadRecordsForDomain = async (domains: Domain[]) => {
    setRecordsLoading(true)
    setError(null)
    const domainName = domains[0].name
    
    try {
      // 并行加载所有提供商的记录
      const recordsPromises = domains.map(domain => 
        api.getDNSRecords(domain.provider, domain.id)
      )
      const responses = await Promise.all(recordsPromises)
      
      // 合并所有记录
      const allRecords: DNSRecord[] = []
      responses.forEach(response => {
        if (response.success && response.data) {
          allRecords.push(...response.data.records)
        }
      })
      
      // 更新缓存
      setRecordsCache(prev => ({
        ...prev,
        [domainName]: {
          records: allRecords,
          timestamp: Date.now()
        }
      }))
      
      // 只有当前选中的域名还是请求时的域名时，才更新显示的记录
      // 这样可以避免用户切换域名后，旧的请求覆盖新域名的数据
      setSelectedDomains(currentDomains => {
        if (currentDomains.length > 0 && currentDomains[0].name === domainName) {
          setRecords(allRecords)
        }
        return currentDomains
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : '加载 DNS 记录失败')
      // 只有当前选中的域名还是请求时的域名时，才清空记录
      setSelectedDomains(currentDomains => {
        if (currentDomains.length > 0 && currentDomains[0].name === domainName) {
          setRecords([])
        }
        return currentDomains
      })
    } finally {
      setRecordsLoading(false)
    }
  }

  // 刷新当前选中域名的记录
  const refreshRecords = useCallback(() => {
    if (selectedDomains.length > 0) {
      loadRecordsForDomain(selectedDomains)
    }
  }, [selectedDomains])

  const handleCreateRecord = () => {
    setEditingRecord(null)
    setIsDialogOpen(true)
  }

  const handleEditRecord = (record: DNSRecord) => {
    setEditingRecord(record)
    setIsDialogOpen(true)
  }

  const handleSaveRecord = async (input: DNSRecordInput) => {
    if (!selectedDomains.length) return

    // 找到对应提供商的域名
    const targetDomain = selectedDomains.find(d => d.provider === input.provider)
    if (!targetDomain) return

    if (editingRecord) {
      // 更新记录
      const response = await api.updateDNSRecord(
        targetDomain.provider,
        targetDomain.id,
        editingRecord.id,
        input
      )
      if (response.success) {
        // 刷新当前域名记录
        refreshRecords()
      }
    } else {
      // 创建记录
      const response = await api.createDNSRecord(input)
      if (response.success) {
        // 刷新当前域名记录
        refreshRecords()
      }
    }
  }

  const handleDeleteRecord = async (record: DNSRecord) => {
    if (!selectedDomains.length) return
    if (!confirm(`确定要删除记录 ${record.name} (${record.type}) 吗？`)) return

    try {
      // 找到对应提供商的域名
      const targetDomain = selectedDomains.find(d => d.provider === record.provider)
      if (!targetDomain) return

      const response = await api.deleteDNSRecord(targetDomain.provider, targetDomain.id, record.id)
      if (response.success) {
        // 刷新当前域名记录
        refreshRecords()
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : '删除失败')
    }
  }

  const getProviderBadgeColor = (provider: DNSProvider) => {
    switch (provider) {
      case DNSProvider.CLOUDFLARE:
        return 'bg-orange-100 text-orange-800'
      case DNSProvider.DNSPOD:
        return 'bg-blue-100 text-blue-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800'
      case 'paused':
        return 'bg-yellow-100 text-yellow-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  // 提供商选择选项
  const providerOptions: SelectOption[] = [
    { 
      value: 'all', 
      label: '所有提供商',
      icon: (
        <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      )
    },
    { 
      value: DNSProvider.CLOUDFLARE, 
      label: 'Cloudflare',
      icon: (
        <div className="w-4 h-4 rounded-full bg-orange-100 flex items-center justify-center">
          <div className="w-2 h-2 rounded-full bg-orange-500"></div>
        </div>
      )
    },
    { 
      value: DNSProvider.DNSPOD, 
      label: 'DNSPod',
      icon: (
        <div className="w-4 h-4 rounded-full bg-blue-100 flex items-center justify-center">
          <div className="w-2 h-2 rounded-full bg-blue-500"></div>
        </div>
      )
    },
  ]

  return (
    <div className="space-y-4 md:space-y-6">
      {/* 页面标题 */}
      <div>
        <h1 className="text-xl md:text-2xl font-bold text-gray-900">
          DNS 记录管理
        </h1>
        <p className="mt-1 text-sm text-gray-500">统一管理 Cloudflare 和 DNSPod 的 DNS 记录</p>
      </div>

      {/* 域名列表 */}
      <Card title="域名列表">
        {initialLoading && (
          <div className="text-center py-8 text-gray-500 animate-fade-in">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            <div className="mt-2 animate-pulse-soft">加载域名和 DNS 记录中...</div>
          </div>
        )}

        {error && !selectedDomains.length && (
          <div className="bg-gradient-to-r from-red-50 to-red-100/50 border border-red-200 text-red-700 px-4 py-3 rounded-lg shadow-sm animate-fade-in-up">
            <div className="flex items-center gap-2">
              <span className="text-lg">⚠️</span>
              <span>{error}</span>
            </div>
          </div>
        )}

        {!initialLoading && mergedDomains.length === 0 && (
          <div className="text-center py-8 text-gray-500">暂无域名</div>
        )}

        {!initialLoading && mergedDomains.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {mergedDomains.map((mergedDomain) => {
              const isSelected = selectedDomains.length > 0 && 
                selectedDomains[0].name === mergedDomain.name
              const cacheEntry = recordsCache[mergedDomain.name]
              const isLoading = cacheEntry?.loading
              const hasCache = cacheEntry && !isLoading
              
              return (
                <button
                  key={mergedDomain.name}
                  onClick={() => selectDomain(mergedDomain.domains)}
                  className={`text-left p-4 border-2 rounded-xl transition-all duration-300 group ${
                    isSelected
                      ? 'border-primary-500 bg-gradient-to-br from-primary-50 to-primary-100/50 shadow-primary'
                      : 'border-gray-100 hover:border-primary-300 hover:shadow-soft-lg hover:-translate-y-1'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-semibold text-gray-900 break-all">{mergedDomain.name}</h3>
                    {isLoading ? (
                      <span className="ml-2 text-xs text-blue-600 flex-shrink-0" title="加载中">
                        <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      </span>
                    ) : hasCache && (
                      <span className="ml-2 text-xs text-green-600 flex-shrink-0" title="已缓存">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {/* 提供商标签 */}
                    {mergedDomain.domains.map((domain) => (
                      <span
                        key={domain.provider}
                        className={`px-2.5 py-1 text-xs font-medium rounded-full transition-all duration-200 group-hover:scale-105 ${getProviderBadgeColor(
                          domain.provider
                        )}`}
                      >
                        {domain.provider === DNSProvider.CLOUDFLARE ? 'Cloudflare' : 'DNSPod'}
                      </span>
                    ))}
                    {/* 状态标签 */}
                    <span
                      className={`px-2.5 py-1 text-xs font-medium rounded-full transition-all duration-200 group-hover:scale-105 ${getStatusBadgeColor(
                        mergedDomain.status
                      )}`}
                    >
                      {mergedDomain.status === 'active' ? '正常' : mergedDomain.status === 'paused' ? '暂停' : '待定'}
                    </span>
                  </div>
                </button>
              )
            })}
          </div>
        )}
      </Card>

      {/* DNS 记录列表 */}
      {selectedDomains.length > 0 && (
        <Card
          title={`${selectedDomains[0].name} 的 DNS 记录`}
          action={
            <div className="flex items-center space-x-2">
              <button
                onClick={refreshRecords}
                disabled={recordsLoading}
                className="px-3 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 hover:shadow-soft focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 active:scale-95"
                title="刷新记录"
              >
                <svg 
                  className={`w-4 h-4 inline-block transition-transform ${recordsLoading ? 'animate-spin' : 'hover:rotate-180'}`} 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
              <button
                onClick={handleCreateRecord}
                className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg hover:from-primary-700 hover:to-primary-800 hover:shadow-primary hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all duration-300 active:scale-95"
              >
                添加记录
              </button>
            </div>
          }
        >
          {/* 搜索和筛选 */}
          <div className="mb-4 flex flex-col md:flex-row md:items-center md:space-x-4 space-y-2 md:space-y-0">
            <div className="relative flex-1">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                placeholder="搜索记录..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg bg-gray-50/50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent shadow-soft hover:border-primary-300 hover:shadow-soft-lg transition-all duration-300"
              />
            </div>
            <Select
              value={selectedProvider}
              onChange={(value) => setSelectedProvider(value as DNSProvider | 'all')}
              options={providerOptions}
              className="w-full md:w-48"
            />
          </div>

          {recordsLoading && (
            <div className="text-center py-8 text-gray-500 animate-fade-in">
              <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
              <div className="mt-2 animate-pulse-soft">刷新中...</div>
            </div>
          )}

          {!recordsLoading && filteredRecords.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              {searchTerm || selectedProvider !== 'all' ? '没有匹配的记录' : '暂无 DNS 记录'}
            </div>
          )}

          {!recordsLoading && filteredRecords.length > 0 && (
            <div className="overflow-x-auto rounded-lg border border-gray-100">
              <table className="min-w-full divide-y divide-gray-100">
                <thead className="bg-gradient-to-r from-gray-50 to-gray-100/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      类型
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      主机记录
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      记录值
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      TTL
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      提供商
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      状态
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      操作
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-50">
                  {filteredRecords.map((record, index) => (
                    <tr key={record.id} className="hover:bg-gradient-to-r hover:from-primary-50/30 hover:to-transparent transition-all duration-200 group animate-fade-in" style={{ animationDelay: `${index * 30}ms` }}>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="px-2.5 py-1 text-xs font-medium bg-gradient-to-r from-gray-100 to-gray-50 text-gray-800 rounded-lg border border-gray-200 group-hover:border-primary-200 transition-colors">
                          {record.type}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-900 break-all font-mono">{record.name}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-900 break-all max-w-xs font-mono">{record.content}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {record.ttl}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span
                          className={`px-2.5 py-1 text-xs font-medium rounded-full transition-all duration-200 group-hover:scale-105 ${getProviderBadgeColor(
                            record.provider
                          )}`}
                        >
                          {record.provider === DNSProvider.CLOUDFLARE ? 'CF' : 'DNSPod'}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {record.proxied && (
                          <span className="px-2.5 py-1 text-xs font-medium bg-gradient-to-r from-orange-100 to-orange-50 text-orange-800 rounded-lg border border-orange-200 group-hover:scale-105 transition-transform">
                            已代理
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium space-x-2">
                        <button
                          onClick={() => handleEditRecord(record)}
                          className="text-primary-600 hover:text-primary-900 transition-all duration-200 hover:underline hover:scale-105 inline-flex items-center gap-1"
                        >
                          编辑
                        </button>
                        <button
                          onClick={() => handleDeleteRecord(record)}
                          className="text-red-600 hover:text-red-900 transition-all duration-200 hover:underline hover:scale-105 inline-flex items-center gap-1"
                        >
                          删除
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* 记录统计 */}
          {!recordsLoading && filteredRecords.length > 0 && (
            <div className="mt-4 flex items-center justify-between text-sm text-gray-500 p-3 bg-gray-50/50 rounded-lg border border-gray-100 animate-fade-in">
              <span className="flex items-center gap-2">
                <span className="inline-block w-2 h-2 bg-primary-500 rounded-full animate-pulse-soft"></span>
                共 <strong className="text-gray-900">{filteredRecords.length}</strong> 条记录
                {(searchTerm || selectedProvider !== 'all') && records.length !== filteredRecords.length && (
                  <span> (从 {records.length} 条中筛选)</span>
                )}
              </span>
              {recordsCache[selectedDomains[0].name] && (
                <span className="text-xs text-gray-400 flex items-center gap-1">
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                  </svg>
                  缓存于 {new Date(recordsCache[selectedDomains[0].name].timestamp).toLocaleTimeString()}
                </span>
              )}
            </div>
          )}
        </Card>
      )}

      {/* DNS 记录对话框 */}
      {selectedDomains.length > 0 && (
        <DNSRecordDialog
          isOpen={isDialogOpen}
          onClose={() => setIsDialogOpen(false)}
          onSave={handleSaveRecord}
          record={editingRecord}
          domainId={selectedDomains[0].id}
          provider={selectedDomains[0].provider}
          availableProviders={selectedDomains.map(d => d.provider)}
        />
      )}
    </div>
  )
}
